import React from "react";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { Box } from "@mui/material";
import { BaseEntity } from "../../../../models/baseEntity";
import noImage from "../../assets/images/missing-img.png";
import { Modal } from "../../../Modal/Modal";

interface AssetPropertyProps {
  property: string;
  value: string;
}

const AssetProperty: React.FC<AssetPropertyProps> = ({ property, value }) => (
  <Box
    sx={{
      borderBottom: "1px solid gray",
      display: "flex",
      justifyContent: "space-between",
      mb: "12px",
    }}
  >
    <Box sx={{ fontWeight: "bold" }}>{property}</Box>
    <Box>{value}</Box>
  </Box>
);

interface DetailPageHeaderProps {
  entity: Pick<
    BaseEntity,
    | "getDisplayName"
    | "getUniqueId"
    | "getType"
    | "getImage"
    | "createdDate"
    | "createdAt"
    | "changedDate"
    | "updatedAt"
  >;
  title: string;
  onDelete?: () => void;
  onBack?: () => void;
  customImage?: string;
}

export const DetailPageHeader: React.FC<DetailPageHeaderProps> = (props) => {
  const { entity, title, onDelete, onBack, customImage } = props;
  const [showModal, setShowModal] = React.useState(false);

  return (
    <>
      <Modal
        style={{}}
        visible={showModal}
        onClose={() => setShowModal(false)}
        title="Are you sure?"
        confirmText="DELETE"
        onSubmit={onDelete || (() => {})}
        closeOnSubmit={false}
      >
        You are about to delete this super important thingy! Are you sure?
      </Modal>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <button onClick={onBack} className="btn btn-secondary mr-2">
            <FaArrowLeft />
          </button>
        </Box>

        <Box
          sx={(theme) => ({
            color: theme.palette.primary.main,
            "& button": {
              "&.error": {
                backgroundColor: theme.palette.error.light,
                border: 0,
                marginRight: "0 !important",
              },
              "& svg": {
                verticalAlign: "text-bottom",
              },
            },
          })}
        >
          <button type="submit" className="btn btn-secondary mr-2">
            <FaSave /> Save
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="btn btn-secondary mr-2 error"
            >
              <MdDelete /> Delete
            </button>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          mt: "10px",
        }}
      >
        <Box sx={{ minWidth: "50%" }}>
          <Box sx={{ textTransform: "uppercase", mb: "15px" }}>
            <Box sx={{ fontSize: "10px", color: "gray" }}>{title}</Box>
            <Box sx={{ fontSize: "24px", fontStyle: "italic" }}>
              {entity.getDisplayName()}
            </Box>
          </Box>
          <Box>
            <AssetProperty property="ID" value={entity.getUniqueId()} />
            {(entity as any).externalId && (
              <AssetProperty
                property="External ID"
                value={(entity as any).externalId || ""}
              />
            )}
            <AssetProperty property="Type" value={entity.getType()} />
            <AssetProperty
              property="Created"
              value={entity.createdDate ?? entity.createdAt}
            />
            <AssetProperty
              property="Changed"
              value={entity.changedDate ?? entity.updatedAt}
            />
          </Box>
        </Box>

        <Box sx={{ minWidth: "600px", mt: "15px" }}>
          <Box
            className="image"
            sx={{
              position: "relative",
              p: 0,
              height: 0,
              overflow: "hidden",
              backgroundPosition: "center",
              backgroundSize: "cover",
              boxSizing: "content-box",
              transition: "padding-bottom 0.5s ease-out",
              pb: "56.25%",
              width: "100%",
              backgroundImage: `url(${customImage || entity.getImage() || noImage})`,
            }}
          />
        </Box>
      </Box>
    </>
  );
};
